import { useState, type FormEvent } from "react";
import { api } from "../api";
import { money, settlementTotals } from "../payrollPresentation";
import type { SettlementRecord } from "../types";
import { useAcknowledgedMutation } from "./useAcknowledgedMutation";
import { Button } from "./ui/Button";
import { Field, Input } from "./ui/Field";
import { Feedback, TableFrame } from "./ui/Surface";
import { Modal } from "./ui/Modal";

export function PayrollBatchCreation({ suggestedName, records, actorName, refresh, onCreated, onFinished, onClose, restoreFocus }: { suggestedName: string; records: SettlementRecord[]; actorName: string; refresh: () => Promise<void>; onCreated: () => void; onFinished: () => void; onClose: () => void; restoreFocus: () => HTMLElement | null }) {
  const ids = records.map(record => record.id);
  const amount = settlementTotals(records).payableAmount;
  const groups = new Map<string, SettlementRecord[]>();
  for (const record of records) groups.set(record.workerId, [...(groups.get(record.workerId) ?? []), record]);
  const summaries = [...groups].map(([workerId, items]) => {
    const totals = settlementTotals(items);
    return { workerId, name: items[0].workerName,
      count: items.filter(item => item.recordKind === "base").length,
      gross: Math.max(0, totals.grossAmount - totals.deductionAmount + totals.additionAmount),
      manualCount: items.filter(item => item.recordKind === "manual").length,
      manual: items.reduce((sum, item) => sum + (item.manualAmount ?? 0), 0),
    };
  }).sort((a, b) => a.name.localeCompare(b.name, "ko-KR") || a.workerId.localeCompare(b.workerId));
  const hasManual = summaries.some(item => item.manualCount > 0);
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const mutation = useAcknowledgedMutation(refresh, onFinished);
  const validName = name.trim().length > 0 && name.trim().length <= 100;
  const create = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!validName || mutation.busy || mutation.committed) return;
    void mutation.execute(async () => {
      await api.createSettlementBatch({ name: name.trim(), settlementRecordIds: ids, createdBy: actorName });
      onCreated();
    });
  };
  return <Modal title="정산 준비" className="payroll-action-dialog" busy={mutation.busy} onClose={onClose} restoreFocus={restoreFocus}>
    <form noValidate onSubmit={create} data-testid="settlement-batch-creation-form">
      <Field label="정산 묶음 이름" required error={submitted && !validName ? "정산 묶음 이름을 입력해 주세요." : undefined}>
        <Input value={name} maxLength={100} disabled={mutation.busy || mutation.committed}
          placeholder={suggestedName} data-testid="settlement-batch-name" onChange={event => setName(event.target.value)} />
      </Field>
      <p className="payroll-creation-meta"><span>{ids.length.toLocaleString()}건</span><span>이체 예정 {money(amount)}</span></p>
      <TableFrame label="작업자별 정산 요약" className="payroll-creation-summary">
        <thead><tr><th scope="col">작업자</th><th scope="col">작업 건수</th><th scope="col">총 금액 (세전)</th>{hasManual && <th scope="col">수동 정산 (세후)</th>}</tr></thead>
        <tbody>{summaries.map(item => <tr key={item.workerId}>
          <th scope="row">{item.name}</th><td>{item.count.toLocaleString()}건</td><td>{money(item.gross)}</td>
          {hasManual && <td>{item.manual > 0 ? "+" : ""}{money(item.manual)}{item.manualCount > 0 && <small>{item.manualCount.toLocaleString()}건</small>}</td>}
        </tr>)}</tbody>
        <tfoot><tr><th scope="row">합계 ({summaries.length.toLocaleString()}명)</th>
          <td>{summaries.reduce((sum, item) => sum + item.count, 0).toLocaleString()}건</td>
          <td>{money(summaries.reduce((sum, item) => sum + item.gross, 0))}</td>
          {hasManual && <td>{money(summaries.reduce((sum, item) => sum + item.manual, 0))}</td>}
        </tr></tfoot>
      </TableFrame>
      {mutation.busy && <Feedback>{mutation.phase === "writing" ? "정산 묶음을 만들고 있습니다…" : "최신 정산 정보를 확인하고 있습니다…"}</Feedback>}
      {mutation.error && <Feedback tone={mutation.committed ? "info" : "error"}>{mutation.error}{!mutation.committed && <p>결과가 불명확하면 정산 목록을 먼저 확인해 주세요.</p>}{mutation.committed && <Button busy={mutation.busy} onClick={() => void mutation.retry()}>상태 다시 확인</Button>}</Feedback>}
      <div className="payroll-dialog-actions"><Button type="button" disabled={mutation.busy} onClick={onClose}>{mutation.committed ? "닫기" : "취소"}</Button><Button type="submit" variant="primary" disabled={mutation.busy || mutation.committed}>정산 묶음 생성</Button></div>
    </form>
  </Modal>;
}
